// Centralized API Configuration for Frontend
const API_CONFIG = {
  // Base URL for all API calls
  BASE_URL: (process.env.REACT_APP_API_URL || 'http://localhost:5000').trim(),
  
  // Frontend URL
  FRONTEND_URL: (process.env.REACT_APP_FRONTEND_URL || 'http://localhost:5000').trim(),
  
  // Environment
  ENV: process.env.REACT_APP_ENV || 'production',
  
  // API Endpoints
  ENDPOINTS: {
    // Authentication
    AUTH: {
      LOGIN: '/auth/login',
      SIGNUP: '/auth/signup',
      PHONE_LOGIN: '/auth/phone-login',
      VERIFY_OTP: '/auth/verify-otp',
    },
    
    // Products
    PRODUCTS: {
      LIST: '/gold',
      DETAIL: (id) => `/gold/${id}`,
      FEATURED: '/gold?featured=true',
      BEST_SELLING: '/gold?bestSelling=true',
      EVERYDAY: '/gold?everyday=true',
      CAROUSEL: '/crousel',
      GOLD: '/gold',
    },
    
    // Categories
    CATEGORIES: {
      ALL: '/category/getAllCategory',
      PRODUCTS: (categoryId) => `/gold?category=${categoryId}`
    },
    
    // Wishlist
    WISHLIST: {
      BASE: '/wishlist/wishlist',
      USER: (userId) => `/wishlist/wishlist/${userId}`,
      REMOVE: (userId, productId) => `/wishlist/remove/${userId}/${productId}`,
    },
    
    // Cart
    CART: {
      BASE: '/cart',
      ADD: '/cart/add',
      USER: (userId) => `/cart/user/${userId}`,
    },
    
    // Support
    SUPPORT: {
      TICKETS: '/ticket/tickets',
      CREATE_TICKET: '/support',
    },
    
    // Media Management (for homepage - public access)
    MEDIA: {
      FEATURES: '/api/media/features',
      BANNERS: '/api/media/banners',
      LOGO: '/api/media/logo',
      BEST_SELLING: '/api/media/best-selling',
      EVERYDAY: '/api/media/everyday',
      CATEGORIES: '/api/categories',
    },
    
    // Other endpoints
    OTHER: {
      TESTIMONIALS: '/testimonial',
      CAROUSEL: '/crousel',
      INSTAGRAM: '/instagram',
      GOLD_PRICE: '/today-price/PriceRouting',
      ABOUT: '/about',
    },
    
    // WebSocket configuration
    WEBSOCKET: {
      BASE: process.env.REACT_APP_WS_URL || 'wss://localhost:5000',
      GOLD_PRICE: process.env.REACT_APP_WS_URL ? `${process.env.REACT_APP_WS_URL}/ws/goldprice` : 'wss://localhost:5000/ws/goldprice',
    },
  },
  
  // Request Configuration
  REQUEST_CONFIG: {
    TIMEOUT: 30000,
    HEADERS: {
      'Content-Type': 'application/json',
    },
  },
};

// Debug: Log the configuration values
console.log("🔧 API_CONFIG loaded:", {
  BASE_URL: API_CONFIG.BASE_URL,
  FRONTEND_URL: API_CONFIG.FRONTEND_URL,
  ENV: API_CONFIG.ENV,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  REACT_APP_FRONTEND_URL: process.env.REACT_APP_FRONTEND_URL,
  REACT_APP_ENV: process.env.REACT_APP_ENV
});

// Helper function to get full API URL
export const getApiUrl = (endpoint) => {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  console.log("🔧 getApiUrl called:", { endpoint, baseUrl: API_CONFIG.BASE_URL, fullUrl: url });
  return url;
};

// Helper function to get endpoint with parameters
export const getEndpoint = (endpoint, ...params) => {
  if (typeof endpoint === 'function') {
    return endpoint(...params);
  }
  return endpoint;
};

// Helper function to get full endpoint URL
export const getFullEndpointUrl = (endpoint, ...params) => {
  const endpointPath = getEndpoint(endpoint, ...params);
  return getApiUrl(endpointPath);
};

// Legacy exports for backward compatibility
export const BACKEND_URL = API_CONFIG.BASE_URL;
export const FRONTEND_URL = API_CONFIG.FRONTEND_URL;

// Authentication endpoints
export const AUTH = {
  LOGIN: getFullEndpointUrl(API_CONFIG.ENDPOINTS.AUTH.LOGIN),
  SIGNUP: getFullEndpointUrl(API_CONFIG.ENDPOINTS.AUTH.SIGNUP),
  PHONE_LOGIN: getFullEndpointUrl(API_CONFIG.ENDPOINTS.AUTH.PHONE_LOGIN),
  VERIFY_OTP: getFullEndpointUrl(API_CONFIG.ENDPOINTS.AUTH.VERIFY_OTP),
};

// Product endpoints
export const PRODUCTS = {
  LIST: getFullEndpointUrl(API_CONFIG.ENDPOINTS.PRODUCTS.LIST),
  DETAIL: (id) => getFullEndpointUrl(API_CONFIG.ENDPOINTS.PRODUCTS.DETAIL, id),
  FEATURED: getFullEndpointUrl(API_CONFIG.ENDPOINTS.PRODUCTS.FEATURED),
  BEST_SELLING: getFullEndpointUrl(API_CONFIG.ENDPOINTS.PRODUCTS.BEST_SELLING),
  EVERYDAY: getFullEndpointUrl(API_CONFIG.ENDPOINTS.PRODUCTS.EVERYDAY),
  CAROUSEL: getFullEndpointUrl(API_CONFIG.ENDPOINTS.PRODUCTS.CAROUSEL),
  GOLD: getFullEndpointUrl(API_CONFIG.ENDPOINTS.PRODUCTS.GOLD),
};

// Category endpoints
export const CATEGORIES = {
  ALL: getFullEndpointUrl(API_CONFIG.ENDPOINTS.CATEGORIES.ALL),
  PRODUCTS: (categoryId) => getFullEndpointUrl(API_CONFIG.ENDPOINTS.CATEGORIES.PRODUCTS, categoryId)
};

// Wishlist endpoints
export const WISHLIST = {
  BASE: getFullEndpointUrl(API_CONFIG.ENDPOINTS.WISHLIST.BASE),
  USER: (userId) => getFullEndpointUrl(API_CONFIG.ENDPOINTS.WISHLIST.USER, userId),
  REMOVE: (userId, productId) => getFullEndpointUrl(API_CONFIG.ENDPOINTS.WISHLIST.REMOVE, userId, productId),
};

// Cart endpoints
export const CART = {
  BASE: getFullEndpointUrl(API_CONFIG.ENDPOINTS.CART.BASE),
  ADD: getFullEndpointUrl(API_CONFIG.ENDPOINTS.CART.ADD),
  USER: (userId) => getFullEndpointUrl(API_CONFIG.ENDPOINTS.CART.USER, userId),
};

// Support endpoints
export const SUPPORT = {
  TICKETS: getFullEndpointUrl(API_CONFIG.ENDPOINTS.SUPPORT.TICKETS),
  CREATE_TICKET: getFullEndpointUrl(API_CONFIG.ENDPOINTS.SUPPORT.CREATE_TICKET),
};

// Media Management endpoints (for homepage - public access)
export const MEDIA = {
  FEATURES: getFullEndpointUrl(API_CONFIG.ENDPOINTS.MEDIA.FEATURES),
  BANNERS: getFullEndpointUrl(API_CONFIG.ENDPOINTS.MEDIA.BANNERS),
  LOGO: getFullEndpointUrl(API_CONFIG.ENDPOINTS.MEDIA.LOGO),
  BEST_SELLING: getFullEndpointUrl(API_CONFIG.ENDPOINTS.MEDIA.BEST_SELLING),
  EVERYDAY: getFullEndpointUrl(API_CONFIG.ENDPOINTS.MEDIA.EVERYDAY),
  CATEGORIES: getFullEndpointUrl(API_CONFIG.ENDPOINTS.MEDIA.CATEGORIES),
};

// Other endpoints
export const OTHER = {
  TESTIMONIALS: getFullEndpointUrl(API_CONFIG.ENDPOINTS.OTHER.TESTIMONIALS),
  CAROUSEL: getFullEndpointUrl(API_CONFIG.ENDPOINTS.OTHER.CAROUSEL),
  INSTAGRAM: getFullEndpointUrl(API_CONFIG.ENDPOINTS.OTHER.INSTAGRAM),
  GOLD_PRICE: getFullEndpointUrl(API_CONFIG.ENDPOINTS.OTHER.GOLD_PRICE),
  ABOUT: getFullEndpointUrl(API_CONFIG.ENDPOINTS.OTHER.ABOUT),
};

// WebSocket configuration
export const WS_CONFIG = {
  BASE: API_CONFIG.ENDPOINTS.WEBSOCKET.BASE,
  GOLD_PRICE: API_CONFIG.ENDPOINTS.WEBSOCKET.GOLD_PRICE,
};

// Log the configuration for debugging
console.log('Frontend API Configuration loaded:', {
  BASE_URL: API_CONFIG.BASE_URL,
  FRONTEND_URL: API_CONFIG.FRONTEND_URL,
  ENV: API_CONFIG.ENV,
  AUTH,
  PRODUCTS,
  CATEGORIES,
  WISHLIST,
  CART,
  SUPPORT,
  MEDIA,
  OTHER,
  WS_CONFIG,
});

export default API_CONFIG;
